import { sha256Hex } from '../digest';
import { type ReleaseStage, type ReleaseTransition, stageBefore } from './release';

/**
 * The two resets, as an operation somebody performs on purpose.
 *
 * ## What this module is, and is not
 *
 * It is the PLAN: what a reset would do, expressed as counts and totals with
 * no personal data in it, and a hash of exactly that. It is not the execution -
 * that lives in the API, against a database, in batches - and it is not a
 * schedule. Nothing here reads a clock and decides it is time.
 *
 * ## Why the plan is hashed and frozen
 *
 * Between reading a plan and agreeing to it, a human looks at numbers. If the
 * apply step recomputed them, the numbers the human agreed to and the numbers
 * the machine acted on would be two different things - and the difference
 * would show up as somebody's Cash. So the plan is written down, hashed, and
 * the apply step is given the hash back: a mismatch is a refusal, not a
 * recalculation.
 *
 * ## Only forwards
 *
 * `closed_alpha -> open_alpha` and `open_alpha -> official`. There is no
 * expression here that yields any other pair, so "reset back to the alpha" and
 * "reset the official version" are not states this codebase can be argued into.
 */

/** The exact sequence. A reset moves one step along it, forwards. */
export const RELEASE_RESET_PATH: Readonly<Record<ReleaseTransition, ReleaseStage>> = Object.freeze({
  open_alpha: 'closed_alpha',
  official: 'open_alpha',
});

/**
 * Is this a reset the game can perform?
 *
 * The only two pairs are `closed_alpha -> open_alpha` and
 * `open_alpha -> official`. Everything else - a repeat, a step backwards, a
 * jump over the open alpha, anything out of `official` - is refused, and
 * refused HERE rather than by a check somebody could forget at a call site.
 */
export function isAllowedResetTransition(from: string, to: string): boolean {
  const destination = to as ReleaseTransition;
  if (destination !== 'open_alpha' && destination !== 'official') {
    return false;
  }
  return RELEASE_RESET_PATH[destination] === from;
}

/** The stage a reset must be RUNNING in to be allowed to perform it. */
export function stageRequiredFor(destination: ReleaseTransition): ReleaseStage {
  return stageBefore(destination);
}

/* -------------------------------------------------------------------------- */
/*  The plan                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Why a reset may not run. Every one is fail-closed.
 *
 * A closed list, because the operator reads it and the tool refuses on any of
 * them. Nothing here is a warning: a blocker blocks.
 */
export const RESET_BLOCKERS = [
  /** The API is still serving players. */
  'maintenance_off',
  /** The shop can still take money. */
  'store_enabled',
  /** The configured stage is not the one this reset leaves. */
  'wrong_stage',
  /** Not one of the two forward steps. */
  'illegal_transition',
  /** Another run is planned or running. */
  'reset_already_active',
  /** An order is still moving, or a human has to look at it. */
  'orders_unsettled',
  /** A payment was approved and its benefit was never delivered. */
  'payment_not_granted',
  /** A wallet's balance and its ledger do not agree. */
  'ledger_mismatch',
  /** A migration has not been applied. */
  'migrations_pending',
  /** An item row is in a state the inventory cannot explain. */
  'inventory_inconsistent',
  /** An eligible purchase has no provable stage of origin. */
  'purchase_without_stage',
] as const;
export type ResetBlocker = (typeof RESET_BLOCKERS)[number];

/**
 * What a reset would do, in counts and totals.
 *
 * Deliberately free of anything personal: no account id, no e-mail, no display
 * name, no payment reference, no per-player figure. An operator needs to know
 * the SHAPE of what is about to happen, and a plan that carried a roster would
 * be a roster somebody pastes into a chat window.
 *
 * Every quantity of currency is a decimal string, like every balance on every
 * wire here: a JSON number is a double to most tooling in between.
 */
export interface ResetPlan {
  /** `open_alpha` or `official`. */
  readonly transition: ReleaseTransition;
  readonly fromStage: ReleaseStage;
  readonly toStage: ReleaseStage;
  /** Which database this was computed against. Host and name, never a password. */
  readonly database: string;
  readonly environment: string;
  /** The instant the plan was computed. Everything below is as of then. */
  readonly cutoffAt: string;
  readonly accountCount: number;
  /** Gold that will be removed, summed across every account. */
  readonly goldToRemove: string;
  /** Cash that will be removed BEFORE the credit. */
  readonly cashToRemove: string;
  /** The base the credit is computed from: eligible direct purchases. */
  readonly eligibleCash: string;
  /** `floor(eligibleCash * multiplier / 10000)`. What will be credited. */
  readonly cashToCredit: string;
  readonly multiplierBasisPoints: number;
  /** How many item rows will be retired, by category. */
  readonly itemsByCategory: Readonly<Record<string, number>>;
  readonly stacksToConsume: number;
  readonly huntSessionsToEnd: number;
  readonly listingsToCancel: number;
  readonly tradesToCancel: number;
  readonly reservationsToRelease: number;
  /** Accounts whose purchased VIP window survives untouched. */
  readonly accountsWithVip: number;
  readonly starterKitsToGrant: number;
  /**
   * Map-1 unlocks the reset will grant, one per account.
   *
   * Counted separately from `starterKitsToGrant` even though the two are the
   * same number today. They answer different questions - "who gets items" and
   * "who may hunt anywhere at all" - and a plan that conflated them would hide
   * the day one of them stops matching.
   */
  readonly mapUnlocksToGrant: number;
  /** Empty for an applicable plan. Anything here refuses. */
  readonly blockers: readonly ResetBlocker[];
}

/**
 * The plan's canonical text, for hashing.
 *
 * Keys sorted, no whitespace, no formatting choices. Two processes that
 * computed the same plan must produce the same string, or the hash would
 * depend on which one wrote it - and the whole point is that a human and a
 * machine can agree on the same bytes.
 */
export function canonicalResetPlan(plan: ResetPlan): string {
  const entries = Object.entries(plan as unknown as Record<string, unknown>).sort(([a], [b]) =>
    a < b ? -1 : 1,
  );
  const canonical = (value: unknown): unknown => {
    if (Array.isArray(value)) {
      return value.map(canonical);
    }
    if (value !== null && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value as Record<string, unknown>)
          .sort(([a], [b]) => (a < b ? -1 : 1))
          .map(([key, inner]) => [key, canonical(inner)]),
      );
    }
    return value;
  };
  return JSON.stringify(entries.map(([key, value]) => [key, canonical(value)]));
}

/** SHA-256 of the canonical plan. The thing the operator has to hand back. */
export function resetPlanHash(plan: ResetPlan): Promise<string> {
  return sha256Hex(canonicalResetPlan(plan));
}

/**
 * The sentence the operator has to type, exactly.
 *
 * It carries the transition, the number of accounts and the plan hash, so a
 * copied-and-pasted confirmation from yesterday's plan does not match today's.
 * Typing it is the last thing between a command and ten thousand wallets.
 */
export function resetConfirmationPhrase(
  transition: ReleaseTransition,
  accountCount: number,
  planHash: string,
): string {
  return `RESET ${stageRequiredFor(transition)} TO ${transition} — ${accountCount} ACCOUNTS — PLAN ${planHash}`;
}

/* -------------------------------------------------------------------------- */
/*  Maintenance                                                               */
/* -------------------------------------------------------------------------- */

/**
 * The screen a player sees while a reset is being prepared or run.
 *
 * Short on purpose. It says what is happening and that access comes back; it
 * does not explain the reset, because the shop's own rules already did and a
 * maintenance page is not where somebody reads terms.
 *
 * No duration, no countdown, no progress bar: neither the client nor the server
 * knows when a reset finishes, and a bar for something unmeasured is a lie with
 * an animation on it.
 */
export const MAINTENANCE_TITLE = 'MANUTENÇÃO EM ANDAMENTO';
export const MAINTENANCE_BODY = [
  'O Kenocyber está temporariamente fechado enquanto os dados e a nova versão são processados com segurança.',
  'Aguarde alguns minutos e tente novamente.',
] as const;

/**
 * The other reason the game is shut, and it is NOT the same reason.
 *
 * Maintenance is a state somebody chose and will leave. This one means the
 * deployment and the database disagree about which version is live - nobody
 * chose it, no player action ends it, and promising "a few minutes" would be
 * promising something nobody is measuring.
 *
 * It names no stage, no version and no service. A player learns that an update
 * is landing; which one is an internal fact.
 */
export const STAGE_MISMATCH_TITLE = 'ATUALIZAÇÃO EM IMPLANTAÇÃO';
export const STAGE_MISMATCH_BODY = [
  'Uma nova versão do Kenocyber está sendo publicada.',
  'O acesso retornará quando todos os serviços estiverem na mesma versão.',
] as const;

/**
 * The server did not answer at all, which is a third thing.
 *
 * Never dressed up as maintenance. Calling an outage a transition tells a
 * player their data is being rewritten when nobody is rewriting anything, and
 * it hides the one case where retrying immediately is the right move.
 */
export const SERVICE_UNAVAILABLE_TITLE = 'SERVIDOR INDISPONÍVEL';
export const SERVICE_UNAVAILABLE_BODY = [
  'Não foi possível falar com o servidor do Kenocyber.',
  'Verifique sua conexão e tente novamente.',
] as const;

/**
 * The machine reason every blocked route answers with, at HTTP 503.
 *
 * Its own code rather than a generic unavailability: the client has to be able
 * to tell "we are between stages" from "the server fell over", because the
 * first has a screen and the second has a retry.
 */
export const MAINTENANCE_REASON = 'maintenance' as const;

/* -------------------------------------------------------------------------- */
/*  Run and account states                                                    */
/* -------------------------------------------------------------------------- */

export const RESET_RUN_STATUSES = ['planned', 'running', 'completed', 'failed'] as const;
export type ResetRunStatus = (typeof RESET_RUN_STATUSES)[number];

/** A run that is not finished holds the lock and blocks a second one. */
export function isResetRunActive(status: string): boolean {
  return status === 'planned' || status === 'running';
}

export const RESET_ACCOUNT_STATUSES = ['pending', 'completed', 'failed'] as const;
export type ResetAccountStatus = (typeof RESET_ACCOUNT_STATUSES)[number];

/**
 * The idempotency key for one operation on one account in one run.
 *
 * `runId + accountId + operation`, and nothing else. It is derived rather than
 * generated so a retry after a crash produces the SAME key - which is what
 * makes a half-finished account safe to run again: every write it already did
 * replays instead of repeating.
 */
export function resetOperationKey(runId: string, accountId: string, operation: string): string {
  return `reset:${runId}:${accountId}:${operation}`;
}

/* -------------------------------------------------------------------------- */
/*  Per-account freezing                                                      */
/* -------------------------------------------------------------------------- */

/**
 * One account's frozen figures, and the state they were computed from.
 *
 * The aggregate plan cannot notice two accounts SWAPPING balances with each
 * other: the totals stay identical and both players get the wrong answer. This
 * is what does. Each account carries its own numbers and a fingerprint, and an
 * account whose fingerprint moved between the freeze and its turn is not
 * processed at all - not adapted, not estimated, not guessed at.
 */
export interface AccountResetPlan {
  readonly accountId: string;
  readonly goldToRemove: string;
  readonly cashToRemove: string;
  readonly eligibleCash: string;
  readonly cashToCredit: string;
  /** The expiry that must survive UNCHANGED. Null when there is no VIP. */
  readonly vipEndsAt: string | null;
  readonly instancesToRetire: number;
  readonly stacksToConsume: number;
  readonly starterKitVersion: number;
  /** SHA-256 of the state these figures were computed from. */
  readonly stateFingerprint: string;
}

/**
 * The canonical text of one account's plan, for hashing.
 *
 * The fingerprint is deliberately NOT in it: the plan is what will be done, the
 * fingerprint is what it was computed from, and they answer different
 * questions. Comparing plans and comparing states have to stay separable.
 */
export function canonicalAccountPlan(plan: AccountResetPlan): string {
  return JSON.stringify([
    plan.accountId,
    plan.goldToRemove,
    plan.cashToRemove,
    plan.eligibleCash,
    plan.cashToCredit,
    plan.vipEndsAt,
    plan.instancesToRetire,
    plan.stacksToConsume,
    plan.starterKitVersion,
  ]);
}

export function accountPlanHash(plan: AccountResetPlan): Promise<string> {
  return sha256Hex(canonicalAccountPlan(plan));
}

/**
 * The material a fingerprint is taken over.
 *
 * Everything the reset READS and would act differently on: both balances, the
 * exact set of items, the stacks, the VIP window and the purchases that fund
 * the credit. Ordered and joined so two reads of an unchanged account produce
 * the same string, and any change at all produces a different one.
 */
export interface AccountStateMaterial {
  readonly gold: string;
  readonly cash: string;
  /** Instance ids, ASCENDING. Order must not depend on the query planner. */
  readonly instanceIds: readonly string[];
  /** `definitionId:total`, ascending. */
  readonly stacks: readonly string[];
  readonly vipEndsAt: string | null;
  /** `purchaseId:cashAmount:stage`, ascending. The credit's own inputs. */
  readonly purchases: readonly string[];
  readonly activeListings: number;
  readonly activeTrades: number;
  readonly openReservations: number;
}

export function canonicalAccountState(material: AccountStateMaterial): string {
  return JSON.stringify([
    material.gold,
    material.cash,
    [...material.instanceIds].sort(),
    [...material.stacks].sort(),
    material.vipEndsAt,
    [...material.purchases].sort(),
    material.activeListings,
    material.activeTrades,
    material.openReservations,
  ]);
}

export function accountStateFingerprint(
  material: AccountStateMaterial,
  context?: ResetContext,
): Promise<string> {
  /*
   * The CONTEXT is part of the fingerprint, not beside it.
   *
   * A plan frozen under one maintenance, one backup, one schema and one
   * wording must not be applied under another - so a change to any of them
   * moves every account's fingerprint and the run blocks, rather than quietly
   * adapting an old plan to new code.
   */
  const canonical = context
    ? JSON.stringify([canonicalAccountState(material), canonicalResetContext(context)])
    : canonicalAccountState(material);
  return sha256Hex(canonical);
}

/* -------------------------------------------------------------------------- */
/*  Advancing the stage                                                       */
/* -------------------------------------------------------------------------- */

/** Why the stage may not move. Every one is fail-closed. */
export const STAGE_ADVANCE_REFUSALS = [
  /** The database says something other than the stage this run leaves. */
  'stage_mismatch',
  /** Not one of the two forward steps. */
  'illegal_transition',
  /** `official` is the end of the sequence. */
  'terminal_stage',
  /** No completed run authorises this move. */
  'no_completed_run',
  /** The stage is already there: a transition happens once. */
  'already_advanced',
] as const;
export type StageAdvanceRefusal = (typeof STAGE_ADVANCE_REFUSALS)[number];

/**
 * May the authoritative stage move from `current` to `destination`?
 *
 * `runCompleted` is the one that cannot be argued around: the stage advances
 * BECAUSE a reset finished, never because somebody edited a variable. A
 * database that has not run the closed alpha's reset cannot become the open
 * alpha, however the environment is configured.
 */
export function stageAdvanceRefusals(input: {
  readonly current: string;
  readonly destination: string;
  readonly runCompleted: boolean;
}): StageAdvanceRefusal[] {
  const refusals: StageAdvanceRefusal[] = [];
  if (input.current === 'official') {
    // Terminal, and it is the whole point of the official version.
    refusals.push('terminal_stage');
  }
  if (input.current === input.destination) {
    refusals.push('already_advanced');
  }
  if (!isAllowedResetTransition(input.current, input.destination)) {
    refusals.push('illegal_transition');
  }
  if (!input.runCompleted) {
    refusals.push('no_completed_run');
  }
  return refusals;
}

/**
 * Does the configured stage agree with the authoritative one?
 *
 * A disagreement is not something to resolve in favour of either: the process
 * would be pricing, warning and crediting for a game it is not in. It keeps
 * maintenance on and refuses to serve until a human makes them agree.
 */
export function stageConfigurationAgrees(configured: string, authoritative: string): boolean {
  return configured === authoritative;
}

/**
 * The machine reason a process answers with while it disagrees with its data.
 *
 * Distinct from `maintenance`, which is a state somebody chose. This one means
 * nobody chose anything: the deployment believes it is serving one game and the
 * database holds another. Prices, warnings, terms and recredit rates all follow
 * the stage, so serving either answer would be serving a game that does not
 * exist. It refuses, and a human decides which side was wrong.
 *
 * What the client is told is exactly this word. Not which stage the database
 * holds, not which one the process was configured with - a mismatch is an
 * operational fact, and the two values are an internal one.
 */
export const STAGE_MISMATCH_REASON = 'stage_mismatch' as const;

/** Where a player reads the current stage's terms, and accepts them. */
export const RELEASE_TERMS_ROUTE = '/api/terms/release' as const;

/**
 * The machine reason every route answers with while the terms are unaccepted.
 *
 * Its own reason rather than a bare `forbidden`: the client has to tell "you
 * may not do this" from "you have not yet been told what game you are in", and
 * only the second has a screen that fixes itself.
 */
export const RELEASE_TERMS_REQUIRED_REASON = 'release_terms_required' as const;

/* -------------------------------------------------------------------------- */
/*  Maintenance, as a fact rather than a flag                                 */
/* -------------------------------------------------------------------------- */

/**
 * What the database says about the doors.
 *
 * A process flag says a process was started a certain way. It cannot say WHEN
 * the doors closed, which transition they closed for, or whether another
 * operator already closed them for a different one - so a backup taken "during
 * maintenance" was only ever a claim about a hand-typed timestamp.
 */
export interface MaintenanceState {
  readonly active: boolean;
  /** Unpredictable, regenerated on every entry. What a backup is bound to. */
  readonly maintenanceId: string | null;
  readonly fromStage: string | null;
  readonly toStage: string | null;
  readonly enteredAt: Date | null;
}

/** Why maintenance may not be entered. Fail-closed, like everything here. */
export const MAINTENANCE_ENTRY_REFUSALS = [
  /** Already closed, and for a DIFFERENT transition than this one. */
  'active_for_another_transition',
  /** Not one of the two forward steps. */
  'illegal_transition',
  /** The database is not in the stage this transition leaves. */
  'wrong_stage',
] as const;
export type MaintenanceEntryRefusal = (typeof MAINTENANCE_ENTRY_REFUSALS)[number];

export function maintenanceEntryRefusals(input: {
  readonly current: MaintenanceState;
  readonly authoritativeStage: string;
  readonly destination: string;
}): MaintenanceEntryRefusal[] {
  const refusals: MaintenanceEntryRefusal[] = [];
  if (input.current.active && input.current.toStage !== input.destination) {
    /*
     * Re-entering for the SAME transition is harmless - an operator repeating a
     * command must not be punished for it - but closing the doors a second time
     * for a different destination would leave two backups, two ids and no way
     * to tell which authorised what.
     */
    refusals.push('active_for_another_transition');
  }
  if (!isAllowedResetTransition(input.authoritativeStage, input.destination)) {
    refusals.push('illegal_transition');
  }
  if (input.authoritativeStage !== RELEASE_RESET_PATH[input.destination as ReleaseTransition]) {
    refusals.push('wrong_stage');
  }
  return refusals;
}

/**
 * Why maintenance may not be LEFT yet.
 *
 * Leaving is a separate, deliberate act: `finalize` never opens the doors. A
 * reset that finished and a game that is ready to serve players are two
 * different states, and the gap between them is where the deployment gets
 * updated and restarted.
 */
export const MAINTENANCE_EXIT_REFUSALS = [
  /** Nothing to leave. */
  'not_active',
  /** The run has not completed, so the stage never moved. */
  'run_not_completed',
  /** The database moved and the deployment still declares the old stage. */
  'stage_configuration_mismatch',
  /** No verified report to point at. */
  'report_missing',
] as const;
export type MaintenanceExitRefusal = (typeof MAINTENANCE_EXIT_REFUSALS)[number];

export function maintenanceExitRefusals(input: {
  readonly active: boolean;
  readonly runCompleted: boolean;
  readonly authoritativeStage: string;
  readonly configuredStage: string;
  readonly reportHash: string | null;
}): MaintenanceExitRefusal[] {
  const refusals: MaintenanceExitRefusal[] = [];
  if (!input.active) {
    refusals.push('not_active');
  }
  if (!input.runCompleted) {
    refusals.push('run_not_completed');
  }
  if (!stageConfigurationAgrees(input.configuredStage, input.authoritativeStage)) {
    /*
     * The expected divergence, and the reason leaving is not automatic: after
     * `finalize` the database is in the new stage while the deployment still
     * declares the old one. Opening the doors then would serve a game whose
     * prices, warnings and credits belong to a different stage.
     */
    refusals.push('stage_configuration_mismatch');
  }
  if (!input.reportHash) {
    refusals.push('report_missing');
  }
  return refusals;
}

/* -------------------------------------------------------------------------- */
/*  Cancelling a maintenance that never became a reset                        */
/* -------------------------------------------------------------------------- */

/** The append-only event kind. Never folded into an ordinary exit. */
export const MAINTENANCE_CANCELLED_EVENT = 'maintenance_cancelled_without_reset' as const;

/**
 * Why a maintenance may not be CANCELLED.
 *
 * ## Not a softer exit
 *
 * `--exit-maintenance` demands a completed run and its verified report, because
 * after a reset, reopening the game is a decision taken having read the
 * verification. That is untouched.
 *
 * This is the opposite operation, for the opposite situation: the doors were
 * shut and nothing was ever done. A rehearsal, a postponement, a transition
 * called off. It demands that no run, no frozen plan, no processed account, no
 * economic movement and no stage change ever began - and the moment any
 * evidence of one exists, it refuses. Neither command can do the other's job,
 * and there is deliberately no flag that makes either give way.
 *
 * ## Why the id has to be typed
 *
 * The operator must name the maintenance they mean. It is 128 unpredictable
 * bits issued when the doors closed, so naming it is proof of having looked at
 * the state being cancelled rather than at a state that has since been replaced
 * by somebody else's.
 */
export const MAINTENANCE_CANCEL_REFUSALS = [
  /** The doors are already open. There is nothing to cancel. */
  'not_active',
  /** The id given is not the maintenance that is running. */
  'maintenance_mismatch',
  /** An audit row that does not say why explains nothing. */
  'reason_missing',
  /** A run exists - planned, running, blocked or finished. */
  'reset_started',
  /** A per-account plan was frozen, or an account was processed. */
  'accounts_frozen',
  /** The reset moved money. Nothing about this is cancellable any more. */
  'economy_touched',
  /** The stage already advanced: this maintenance did its job. */
  'stage_advanced',
  /** The deployment and the database disagree; a human settles that first. */
  'stage_configuration_mismatch',
] as const;
export type MaintenanceCancelRefusal = (typeof MAINTENANCE_CANCEL_REFUSALS)[number];

/**
 * Every reason this cancellation may not happen, computed from FACTS.
 *
 * Exhaustive rather than short-circuiting: an operator holding a shut game
 * wants the whole list at once, not one reason per attempt.
 */
export function maintenanceCancelRefusals(input: {
  readonly current: MaintenanceState;
  /** The id the operator typed. Must match the running maintenance exactly. */
  readonly maintenanceId: string;
  readonly reason: string;
  /** Runs that exist at all, in any status. Zero is the only acceptable count. */
  readonly resetRuns: number;
  /** Frozen per-account rows, in any status. */
  readonly resetAccounts: number;
  /** Ledger movements the reset wrote. One is one too many. */
  readonly resetLedgerEntries: number;
  /** Stage moves recorded since the doors closed. */
  readonly stageEvents: number;
  readonly authoritativeStage: string;
  readonly configuredStage: string;
}): MaintenanceCancelRefusal[] {
  const refusals: MaintenanceCancelRefusal[] = [];
  if (!input.current.active) {
    refusals.push('not_active');
  }
  if (!input.current.maintenanceId || input.current.maintenanceId !== input.maintenanceId) {
    /*
     * Naming the wrong one is not a typo to be forgiven. An operator who has
     * the id of a maintenance that is no longer running is looking at a state
     * that has been replaced, and cancelling the current one on their behalf
     * would be acting on a decision nobody made about it.
     */
    refusals.push('maintenance_mismatch');
  }
  if (input.reason.trim().length === 0) {
    refusals.push('reason_missing');
  }
  if (input.resetRuns > 0) {
    refusals.push('reset_started');
  }
  if (input.resetAccounts > 0) {
    refusals.push('accounts_frozen');
  }
  if (input.resetLedgerEntries > 0) {
    refusals.push('economy_touched');
  }
  if (input.stageEvents > 0) {
    refusals.push('stage_advanced');
  }
  if (!stageConfigurationAgrees(input.configuredStage, input.authoritativeStage)) {
    refusals.push('stage_configuration_mismatch');
  }
  return refusals;
}

/**
 * The material a per-account fingerprint is taken over, plus the CONTEXT the
 * plan was frozen under.
 *
 * The context matters as much as the state: a plan frozen under one
 * maintenance, one backup, one set of migrations and one wording must not be
 * applied under another. Adapting an old plan to new code is exactly the
 * silent behaviour this exists to prevent.
 */
export interface ResetContext {
  readonly maintenanceId: string;
  /** SHA-256 of the manifest the plan was prepared with. */
  readonly manifestHash: string;
  readonly fromStage: string;
  readonly toStage: string;
  readonly termsVersion: string;
  /** How many migrations the schema was at. */
  readonly migrationsVersion: number;
  /** The reset rules' own version. Bumped when the mechanism changes. */
  readonly rulesVersion: number;
}

/**
 * The version of the reset RULES themselves.
 *
 * Bumped whenever what a reset does changes - a new wipe, a different order, a
 * changed formula. A plan frozen under one version cannot be applied under
 * another, because "the same numbers" would no longer mean the same actions.
 */
export const RESET_RULES_VERSION = 3;

/** The context, canonically, so two processes hash it identically. */
export function canonicalResetContext(context: ResetContext): string {
  return JSON.stringify([
    context.maintenanceId,
    context.manifestHash,
    context.fromStage,
    context.toStage,
    context.termsVersion,
    context.migrationsVersion,
    context.rulesVersion,
  ]);
}

export function resetContextHash(context: ResetContext): Promise<string> {
  return sha256Hex(canonicalResetContext(context));
}
